import { api, Badge, Banner, dateTime, PageHead, useAsync } from "@getaways/extranet-ui";
import { useState } from "react";
import { useCan } from "../lib/access";
import { int } from "../lib/format";

export interface Status {
  asOf: string | null;
  late: boolean;
  sync: boolean;
  sources: { source: string; cursor: { at: string; id: string } | null; lastRunAt: string | null; lastOkAt: string | null; rows: number; error: string | null }[];
}

const SOURCE_NAMES: Record<string, string> = { orders: "Platform orders", payments: "Payments", desk_sales: "Booking Desk sales", partners_ledger: "Yes Partners commissions" };

/** How fresh the numbers are: each source the sync follows (docs/36 §3). */
export function StatusPage({ onSynced }: { onSynced: () => void }) {
  const can = useCan();
  const { data, error, reload } = useAsync(() => api<Status>("GET", "/v1/status"), []);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string }>();
  const [busy, setBusy] = useState(false);
  const run = async (full: boolean) => {
    setBusy(true);
    try {
      const r = await api<{ ran: boolean; reason?: string; result?: { orders: number; ms: number } }>("POST", "/v1/sync", { full });
      setNotice(r.ran ? { tone: "ok", text: `${int(r.result?.orders ?? 0)} orders derived in ${int(r.result?.ms ?? 0)} ms.` } : { tone: "bad", text: r.reason ?? "Not run." });
      reload();
      onSynced();
    } catch (err) {
      setNotice({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <PageHead title="Data status" sub={data?.asOf ? `Numbers as of ${dateTime(data.asOf, "Europe/Lisbon")}` : "Not synced yet"}>
        {can("sync:manage") && data?.sync && (
          <>
            <button disabled={busy} onClick={() => void run(false)}>Sync now</button>
            <button disabled={busy} onClick={() => void run(true)} title="Derive every order again">Rebuild</button>
          </>
        )}
      </PageHead>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {notice && <Banner notice={notice} onClose={() => setNotice(undefined)} />}
      <div className="card">
        <p className="small muted">The reports read their own copy of the platform's data, kept up to date every minute. Dashboards never query the live databases.</p>
        <table className="table">
          <thead><tr><th>Source</th><th>Read up to</th><th>Last pass</th><th className="num">Rows last pass</th><th>State</th></tr></thead>
          <tbody>
            {(data?.sources ?? []).map((s) => (
              <tr key={s.source}>
                <td>{SOURCE_NAMES[s.source] ?? s.source}</td>
                <td className="small">{s.cursor ? dateTime(s.cursor.at, "Europe/Lisbon") : "—"}</td>
                <td className="small">{dateTime(s.lastRunAt ?? undefined, "Europe/Lisbon")}</td>
                <td className="num">{int(s.rows)}</td>
                <td>{s.error ? <Badge tone="bad" title={s.error}>error</Badge> : <Badge tone="ok">ok</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
